import { ComponentType } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';
import {
  DialogRef,
  DialogService,
  IAlertConfig,
  IConfirmConfig,
  IDialogConfig,
  IGuideConfig,
  ITooltipConfig
} from '@partners/ui';
import { AlertComponent } from '../alert/alert.component';
import { ConfirmComponent } from '../confirm/confirm.component';
import { GuideComponent } from '../guide/guide.component';
import { TooltipComponent } from '../tooltip/tooltip.component';

const DIALOG_DEFAULT_CONFIG = {
  backdropClass: 'dialog-backdrop',
  panelClass: 'dialog-panel'
};

const TIP_DEFAULT_CONFIG = {
  backdropClass: 'tooltip-backdrop',
  panelClass: 'tooltip-panel',
  maxWidth: '15rem',
  minHeight: '3rem',
  hideCloseIcon: true,
  hideIcon: false
};

const ALERT_DEFAULT_CONFIG = {
  panelClass: 'alert-panel',
  buttons: {
    ok: 'אישור'
  }
};

const GUIDE_DEFAULT_CONFIG = {
  panelClass: 'guide-panel',
  buttons: {
    ok: 'אישור'
  }
};

const CONFIRM_DEFAULT_CONFIG = {
  panelClass: 'confirm-panel',
  buttons: {
    confirm: 'אישור',
    cancel: 'ביטול'
  }
};

/**
 * @description
 * an adapter to dialog library interface, this service provides "shortcuts" to common dialog behavior
 * from here we can directly load a typed dialog, such as alert  | confirm with their common configuration
 * without specifying again and again their basic requirements & configuration
 */

@Injectable({
  providedIn: 'root'
})
export class DialogAdapter {
  private _dialogService = inject(DialogService);

  #dialogRef: DialogRef = undefined;

  constructor() {}

  open<T = any>(component: ComponentType<T>, dialogConfig?: IDialogConfig): DialogRef {
    return this._dialogService.open(component, dialogConfig);
  }

  /**
   * @description
   * show a common alert dialog, extends alert's common configuration with custom args from the consumer
   * @param alertConfig partial or full alert config to override default
   * @returns a common dialog ref with access to its closing method and data
   */
  openAlert<T = any>(alertConfig?: IAlertConfig): DialogRef {
    alertConfig = Object.assign({ ...DIALOG_DEFAULT_CONFIG }, { ...ALERT_DEFAULT_CONFIG }, { ...alertConfig });
    return this._dialogService.open(AlertComponent, alertConfig);
  }

  openGuide(guideConfig: IGuideConfig): DialogRef {
    guideConfig = Object.assign({ ...DIALOG_DEFAULT_CONFIG }, { ...GUIDE_DEFAULT_CONFIG }, { ...guideConfig });
    return this._dialogService.open(GuideComponent, guideConfig);
  }

  openTooltip<T = any>(tipConfig?: ITooltipConfig): DialogRef {
    tipConfig = Object.assign({ ...DIALOG_DEFAULT_CONFIG }, { ...TIP_DEFAULT_CONFIG }, { ...tipConfig });
    return this._dialogService.open(TooltipComponent, tipConfig);
  }

  /**
   * @description
   * show a common confirm dialog, extends confirm's common configuration with custom args from the consumer
   * @param confirmConfig partial or full confirm config to override default
   * @returns a common dialog ref with access to its closing method and data
   */
  openConfirm<T = any>(confirmConfig?: IConfirmConfig): DialogRef {
    confirmConfig = Object.assign({ ...CONFIRM_DEFAULT_CONFIG }, { ...confirmConfig });
    return this._dialogService.open(ConfirmComponent, confirmConfig);
  }
  /**
   * @description
   * Notify the user about unauthorized back navigation attempt.
   * for example by pressing the back navigation arrow of the browser.
   */
  openIllegalNavigationAlert() {
    //prevent popups duplication
    if (this.#dialogRef) {
      this.#dialogRef.dismiss();
    }
    //
    this.#dialogRef = this.openAlert({ title: 'לתשומת ליבך', message: 'אי אפשר לחזור ולערוך פרטים שכבר הזנת' });
  }
}
